/**
 * React hook for Pyodide-based validation.
 *
 * Manages a Web Worker running Pyodide for schema validation.
 * Provides async validation API and handles worker lifecycle.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// Singleton worker instance (shared across all hook instances)
let globalWorker = null;
let globalWorkerPromise = null;
let globalInitPromise = null;
let pendingCallbacks = new Map();
let messageIdCounter = 0;

/**
 * Get or create the shared Pyodide worker
 */
function getSharedWorker() {
  if (globalWorker !== null) {
    return globalWorker;
  }

  if (globalWorkerPromise !== null) {
    return globalWorkerPromise;
  }

  globalWorkerPromise = new Promise((resolve) => {
    const worker = new Worker(
      new URL('../workers/pyodideWorker.js', import.meta.url),
      { type: 'classic' }
    );

    worker.onmessage = (event) => {
      const { id, type, success, error, ...data } = event.data;

      // Handle ready message
      if (type === 'ready') {
        globalWorker = worker;
        resolve(worker);
        return;
      }

      // Handle response to a request
      if (id !== undefined) {
        const callback = pendingCallbacks.get(id);
        if (callback) {
          if (success) {
            callback.resolve(data);
          } else {
            callback.reject(new Error(error));
          }
          pendingCallbacks.delete(id);
        }
      }
    };

    worker.onerror = (error) => {
      console.error('Pyodide worker error:', error);
      // Reject all pending callbacks
      for (const [id, callback] of pendingCallbacks) {
        callback.reject(new Error('Worker error'));
        pendingCallbacks.delete(id);
      }
    };
  });

  return globalWorkerPromise;
}

/**
 * Send a message to the worker and wait for response
 */
async function sendMessage(type, payload = {}) {
  const worker = await getSharedWorker();
  const id = ++messageIdCounter;

  return new Promise((resolve, reject) => {
    pendingCallbacks.set(id, { resolve, reject });
    worker.postMessage({ type, payload, id });
  });
}

/**
 * Initialize Pyodide worker (called once, globally)
 */
async function initPyodideWorker() {
  if (globalInitPromise !== null) {
    return globalInitPromise;
  }

  globalInitPromise = sendMessage('init');
  return globalInitPromise;
}

/**
 * Hook for Pyodide validation
 *
 * @returns {Object} { isReady, isLoading, error, schemaVersion, validate, reload }
 */
export function usePyodide() {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schemaVersion, setSchemaVersion] = useState(null);
  const mountedRef = useRef(true);

  /**
   * Clean up Python exception messages for display
   */
  const cleanErrorMessage = (msg) => {
    if (!msg) return 'Unknown error';

    // Handle common Python exception formats
    // e.g., "ValueError: some message" -> "some message"
    const valueErrorMatch = msg.match(/(?:ValueError|TypeError|KeyError|AttributeError|RuntimeError):\s*(.+)/);
    if (valueErrorMatch) {
      return valueErrorMatch[1].trim();
    }

    // Handle "Value error, message" format from Pydantic
    const pydanticMatch = msg.match(/Value error,\s*(.+)/i);
    if (pydanticMatch) {
      return pydanticMatch[1].trim();
    }

    // Remove Python traceback lines
    const lines = msg.split('\n');
    // Find the actual error message (usually the last non-empty line that isn't traceback)
    const errorLines = lines.filter(line =>
      line.trim() &&
      !line.includes('Traceback') &&
      !line.includes('File "') &&
      !line.startsWith('    ') &&
      !line.startsWith('  ') &&
      !line.includes('PythonError:')
    );

    if (errorLines.length > 0) {
      const lastLine = errorLines[errorLines.length - 1];
      // Try to extract just the message part after exception type
      const colonMatch = lastLine.match(/^\w+Error:\s*(.+)/);
      return colonMatch ? colonMatch[1].trim() : lastLine.trim();
    }

    // Fallback: return first 100 chars
    return msg.length > 100 ? msg.substring(0, 100) + '...' : msg;
  };

  // Initialize on mount
  useEffect(() => {
    mountedRef.current = true;

    initPyodideWorker()
      .then((result) => {
        if (mountedRef.current) {
          console.log('Pyodide initialized successfully, schema version:', result.schemaVersion);
          setIsReady(true);
          setIsLoading(false);
          setSchemaVersion(result.schemaVersion);
        }
      })
      .catch((err) => {
        console.error('Failed to initialize Pyodide:', err);
        if (mountedRef.current) {
          // Provide user-friendly error messages for common issues
          let errorMsg = cleanErrorMessage(err.message);
          if (errorMsg.includes('field_validator') || errorMsg.includes('pydantic')) {
            errorMsg = 'Schema validation requires Pydantic v2. Try clearing browser cache and refreshing.';
          }
          setError(errorMsg);
          setIsLoading(false);
        }
      });

    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Validate content using Pyodide schemas
   *
   * @param {string} contentType - 'agent', 'skill', 'prompt', or 'judge'
   * @param {string} content - Full markdown content with YAML frontmatter
   * @returns {Promise<Array>} Array of error objects
   */
  const validate = useCallback(async (contentType, content) => {
    if (!isReady) {
      return [];
    }

    try {
      const result = await sendMessage('validate', { contentType, content });
      // Clean up error messages
      return (result.errors || []).map(err => ({
        ...err,
        message: err.message ? cleanErrorMessage(err.message) : err.message,
      }));
    } catch (err) {
      console.error('Pyodide validation error:', err);
      // Return a user-friendly error
      return [{
        line: 1,
        message: `Validation error: ${cleanErrorMessage(err.message)}`,
        severity: 'error',
        source: 'pyodide',
      }];
    }
  }, [isReady]);

  /**
   * Reload schemas from server
   */
  const reload = useCallback(async () => {
    if (!isReady) {
      return;
    }

    try {
      const result = await sendMessage('reload');
      setSchemaVersion(result.schemaVersion);
    } catch (err) {
      console.error('Failed to reload schemas:', err);
    }
  }, [isReady]);

  return {
    isReady,
    isLoading,
    error,
    schemaVersion,
    validate,
    reload,
  };
}

export default usePyodide;
